import React, { useMemo, useState } from "react";
import { FiActivity, FiUser, FiChevronDown, FiChevronRight } from "react-icons/fi";

const safeParse = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;

    if (typeof value === "string") {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }

    return null;
};

const formatDateTime = (date) => {
    if (!date) return "—";

    return new Date(date).toLocaleString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatAction = (action) => {
    if (!action) return "Activity";

    return action
        .replace(/_/g, " ")
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const parseLogDetails = (details) => {
    if (!details) return "No details";

    if (typeof details === "string") {
        try {
            const parsed = JSON.parse(details);
            return parsed.message || parsed.details || details;
        } catch {
            return details;
        }
    }

    if (typeof details === "object") {
        return details.message || details.details || JSON.stringify(details);
    }

    return String(details);
};

const formatValue = (value) => {
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value === null || value === undefined || value === "") return "—";
    return String(value);
};

const getChangedFields = (log) => {
    if (!log.old_values || !log.new_values) return [];

    const oldVals = safeParse(log.old_values);
    const newVals = safeParse(log.new_values);

    if (!oldVals || !newVals) return [];

    const keys = log.changed_fields || Object.keys(newVals);

    return keys.map((key) => ({
        field: key,
        oldValue: oldVals?.[key],
        newValue: newVals?.[key],
    }));
};

const normalizeSearchText = (value) => {
    return String(value || "")
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\s+/g, " ")
        .trim();
};

const LogChanges = ({ changes }) => (
    <div className="mt-2 space-y-1">
        {changes.map((change, i) => (
            <div
                key={i}
                className="text-xs text-gray-600 flex flex-wrap items-center gap-1.5"
            >
                <span className="font-semibold capitalize text-gray-700">
                    {change.field.replace(/_/g, " ")}:
                </span>

                <span className="line-through text-red-500">
                    {formatValue(change.oldValue)}
                </span>

                <span className="text-gray-400">→</span>

                <span className="font-semibold text-green-700">
                    {formatValue(change.newValue)}
                </span>
            </div>
        ))}
    </div>
);

// job_created logs pack in every entered field, so collapse them behind a
// closed-by-default toggle to keep the table scannable. Other actions keep
// showing their (usually short) diff inline.
const CollapsibleLogChanges = ({ changes }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="mt-2">
            <button
                type="button"
                onClick={() => setExpanded(prev => !prev)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
                {expanded ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
                {expanded ? "Hide" : "Show"} entered details ({changes.length})
            </button>

            {expanded && <LogChanges changes={changes} />}
        </div>
    );
};

const ActivityLogsTable = ({ logs = [], showLogs, onToggle }) => {

    const [search, setSearch] = useState("");

    const filteredLogs = useMemo(() => {
        const query = normalizeSearchText(search);

        if (!query) return logs;

        return logs.filter((log) => {
            const changes = getChangedFields(log);

            const searchable = normalizeSearchText([
                log.user_name,
                log.user_role,
                log.action,
                formatAction(log.action),
                parseLogDetails(log.details),
                formatDateTime(log.timestamp),
                ...changes.flatMap(change => [
                    change.field,
                    change.field?.replace(/_/g, " "),
                    formatValue(change.oldValue),
                    formatValue(change.newValue),
                ]),
            ].filter(Boolean).join(" "));

            return searchable.includes(query);
        });
    }, [logs, search]);

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center">
                        <FiActivity className="w-4 h-4" />
                    </div>

                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">
                            Activity Logs
                        </h2>
                        <p className="text-xs text-gray-500">
                            {logs.length} total records
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={onToggle}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 transition-colors"
                >
                    {showLogs ? "Hide" : "Show"}
                </button>
            </div>

            {showLogs && (
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search logs by user, action, date, or details..."
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    />

                    {search.trim() && (
                        <p className="mt-2 text-xs text-gray-500">
                            Showing {filteredLogs.length} of {logs.length} logs
                        </p>
                    )}
                </div>
            )}

            {showLogs && (
                <div className="max-h-96 overflow-auto">
                    {filteredLogs.length === 0 ? (
                        <div className="p-4 text-sm text-gray-500">
                            {search.trim() ? "No matching activity logs." : "No activity logs yet."}
                        </div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr className="border-b border-gray-200">
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Date
                                    </th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        User
                                    </th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Action
                                    </th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Details
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100">
                                {filteredLogs.map((log, index) => {
                                    const changes = getChangedFields(log);

                                    return (
                                        <tr
                                            key={`${log.id || "log"}-${index}`}
                                            className="hover:bg-gray-50 transition-colors align-top"
                                        >
                                            <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                                                {formatDateTime(log.timestamp)}
                                            </td>

                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0">
                                                        <FiUser className="w-3.5 h-3.5" />
                                                    </div>

                                                    <div className="min-w-0">
                                                        <div className="text-sm font-medium text-gray-900 truncate">
                                                            {log.user_name || "Unknown User"}
                                                        </div>

                                                        {log.user_role && (
                                                            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                {log.user_role?.split("_")?.join(" ")}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold whitespace-nowrap">
                                                    {formatAction(log.action)}
                                                </span>
                                            </td>

                                            <td className="px-4 py-3 text-gray-700">
                                                <div className="max-w-3xl">
                                                    <p className="text-sm leading-5">
                                                        {parseLogDetails(log.details)}
                                                    </p>

                                                    {changes.length > 0 && (
                                                        log.action === "job_created"
                                                            ? <CollapsibleLogChanges changes={changes} />
                                                            : <LogChanges changes={changes} />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};

export default ActivityLogsTable;